import { Layout } from "antd";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { useSocketContext } from "@contexts/socket/socketContext";
import { useLatestState } from "@customHooks/useLatestState";
import { BlockItem } from "@pages/KiosViewPage/BlockItem";
import { fetchAPI } from "@utils/https/fetchAPI";

const Container = styled(Layout)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background: black;
`;

const AdsLayout = styled.div`
  position: relative;
  width: ${({ width = "600" }) => width}px;
  height: ${({ height = "600" }) => height}px;
  box-sizing: border-box;
`;

export default () => {
  const socket = useSocketContext();

  const params = useParams();
  const campaignId = _.get(params, ["id"]);
  const [isReady, setIsReady] = useState(false);
  const [campaign, setCampaign] = useState([]);
  const [campaignElapsedTime, setCampaignElapsedTime] = useState(0);
  const [currentCampaignIndex, setCurrentCampaignIndex] = useState(0);
  const intervalRef = useRef(null);
  const [playCampaignCount, setPlayCampaignCount] = useState(1);
  const latestStateRefs = useLatestState({
    states: { playCampaignCount, campaign },
  });
  const handleNextMediaRefs = useRef([]);

  const currentCampaign = _.get(campaign, [currentCampaignIndex]);

  const initData = async () => {
    console.log("init :>> ");
    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/advertising-campaigns-kios-view",
      isAuth: false,
      data: { id: campaignId },
    });
    if (_.get(resp, ["status"])) {
      // find max duration
      const results = _.chain(resp)
        .get(["result"])
        .thru((item) => {
          return [{ ...item }];
        })
        .map((item) => {
          const filteredAdsBlocks = _.filter(
            _.get(item, "adsBlocks"),
            (block) => {
              const blockMedias = _.get(block, "blockMedias");
              return _.size(blockMedias) > 0;
            }
          );

          return { ...item, adsBlocks: filteredAdsBlocks };
        })
        .filter((item) => {
          const adsBlocks = _.get(item, ["adsBlocks"]);
          return _.size(adsBlocks) > 0;
        })
        .map((item) => {
          const maxDuration = _.chain(item)
            .get(["adsBlocks"])
            .map((item) => {
              const blockMedias = _.get(item, ["blockMedias"]);
              return _.sumBy(blockMedias, "duration");
            })
            .max()
            .value();
          return { ...item, max_duration: maxDuration };
        })
        .value();

      setIsReady(_.isEmpty(results) ? false : true);
      setCampaign(results);
    }
  };

  useEffect(() => {
    initData();
  }, []);

  const handleClearCampaignState = () => {
    setCampaignElapsedTime(0);
    setCurrentCampaignIndex(0);
    setPlayCampaignCount(0);
  };

  const handleNextCampaign = () => {
    setCampaignElapsedTime(0);
    setCurrentCampaignIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % _.size(campaign);
      if (nextIndex === 0) {
        setPlayCampaignCount((prev) => prev + 1);
      }
      return nextIndex;
    });

    if (playCampaignCount >= 5) {
      updateMediaLogs();
      handleClearCampaignState();
    }
  };

  useEffect(() => {
    if (_.size(campaign) > 0) {
      const timer = setTimeout(() => {
        handleNextCampaign();
      }, _.get(campaign, [currentCampaignIndex, "max_duration"]) * 1000);

      intervalRef.current = setInterval(() => {
        setCampaignElapsedTime((prev) => prev + 1);
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(intervalRef.current);
      };
    }
  }, [playCampaignCount, currentCampaignIndex, campaign]);

  const updateMediaLogs = async () => {
    const latestStateCampaign = latestStateRefs.current.campaign;
    const latestStatePlayCampaignCount =
      latestStateRefs.current.playCampaignCount;

    const playMediaLogList = _.chain(latestStateCampaign)
      .cloneDeep()
      .flatMap("adsBlocks")
      .flatMap((adsBlock) => {
        const blockMedias = _.get(adsBlock, ["blockMedias"]);
        const campaign_id = _.get(adsBlock, ["advertising_campaign_id"]);
        return _.map(blockMedias, (media) => {
          const media_id = _.get(media, ["media_id"]);
          return {
            campaign_id,
            media_id,
            multiple: latestStatePlayCampaignCount,
            devices: null,
            group: null,
          };
        });
      })
      .value();

    await fetchAPI({
      method: "post",
      path: "/api/v2/player/update-play-media-logs",
      isAuth: false,
      data: { playMediaLogList },
    });
  };

  const onHandleKiosUpdateContent = async ({ massageCampaignId }) => {
    if (massageCampaignId === _.toInteger(campaignId)) {
      await updateMediaLogs();
      await initData();
      handleClearCampaignState();
    }
  };

  useEffect(() => {
    if (!socket) {
      return undefined;
    }
    socket.on(
      "SERVICE-EMIT-TO-KIOS-VIEW-UPDATE-CONTENT",
      ({ message, campaignId: massageCampaignId }) => {
        console.log("message :>> ", message);
        onHandleKiosUpdateContent({ massageCampaignId });
      }
    );

    return () => {
      socket.off("SERVICE-EMIT-TO-KIOS-VIEW-UPDATE-CONTENT");
    };
  }, [socket]);

  return (
    <>
      <Container>
        {isReady ? (
          <AdsLayout
            width={_.get(currentCampaign, ["width"])}
            height={_.get(currentCampaign, ["height"])}
          >
            {!_.chain(currentCampaign).get(["adsBlocks"]).isEmpty().value() &&
              _.chain(currentCampaign)
                .get(["adsBlocks"], [])
                .map((item, index) => {
                  return (
                    <BlockItem
                      index={index}
                      key={index}
                      block={item}
                      playCampaignCount={playCampaignCount}
                      handleNextMediaRef={(el) =>
                        (handleNextMediaRefs.current[index] = el)
                      }
                    />
                  );
                })
                .value()}
          </AdsLayout>
        ) : null}
      </Container>
    </>
  );
};
